// Value formatters for kernel render primitives.
//
// Specs declare format names (strings) so kernel definitions stay
// JSON-serialisable. The primitive picks a formatter at render time.
// Keep this set small and additive — every formatter here becomes a
// stable contract the saved kernels rely on.

import { parseServerTimestamp } from "./traceUtils.js";

const DISPLAY_ACRONYMS = new Set(["ai", "api", "ci", "cd", "llm", "mcp", "qa", "sql", "ui", "ux"]);

const LOCAL_AI_SOFTWARE_LABELS = new Map([
    ["local-llm-torch", "PyTorch"],
    ["local-llm-mlx", "MLX"],
    ["local-llm-ggml", "GGML"],
    ["local-llm-llama", "llama.cpp"],
    ["local-llm-runtime-image", "Local AI runtime"],
    ["local-llm-gpt4all", "GPT4All"],
    ["local-llm-onnx-genai", "ONNX Runtime GenAI"],
    ["local-llm-tensorrt", "TensorRT-LLM"],
    ["local-llm-jaccl", "JACCL"],
    ["local-llm-lmstudio", "LM Studio"],
    ["local-llm-mlc", "MLC LLM"],
    ["local-llm-whisper", "whisper.cpp"],
    ["local-llm-ctranslate2", "CTranslate2"],
    ["local-llm-ollama", "Ollama"],
]);

function humanizeMachineLabel(value) {
    if (value == null) return "";
    const words = String(value).replace(/[_-]+/g, " ").trim().split(/\s+/).filter(Boolean);
    return words.map((word, index) => {
        if (DISPLAY_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
        if (index > 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(" ");
}

const formatters = {
    number: (v) => {
        if (v == null || v === "") return "—";
        const n = Number(v);
        if (!Number.isFinite(n)) return String(v);
        if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
        if (Number.isInteger(n)) return n.toString();
        return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    integer: (v) => {
        if (v == null || v === "") return "—";
        const n = Math.round(Number(v));
        return Number.isFinite(n) ? n.toLocaleString() : String(v);
    },
    percent: (v) => {
        if (v == null || v === "") return "—";
        const n = Number(v);
        if (!Number.isFinite(n)) return String(v);
        // Accept either 0-1 or 0-100; >1 is treated as already-scaled.
        const pct = Math.abs(n) <= 1 ? n * 100 : n;
        return `${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    },
    "0.00": (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : "—";
    },
    "0.0": (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(1) : "—";
    },
    duration_ms: (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return "—";
        if (n < 1000) return `${Math.round(n)} ms`;
        if (n < 60_000) return `${(n / 1000).toFixed(1)} s`;
        if (n < 3_600_000) return `${(n / 60_000).toFixed(1)} m`;
        return `${(n / 3_600_000).toFixed(1)} h`;
    },
    duration_s: (v) => formatters.duration_ms(Number(v) * 1000),
    timestamp: (v) => {
        if (!v) return "—";
        const d = new Date(parseServerTimestamp(v));
        if (Number.isNaN(d.getTime())) return String(v);
        return d.toLocaleString();
    },
    date: (v) => {
        if (!v) return "—";
        const d = new Date(parseServerTimestamp(v));
        if (Number.isNaN(d.getTime())) return String(v);
        return d.toLocaleDateString();
    },
    humanize: humanizeMachineLabel,
    local_ai_software: (v) => {
        if (v == null) return "";
        const rule = String(v).trim().toLowerCase().replace(/_/g, "-");
        const known = LOCAL_AI_SOFTWARE_LABELS.get(rule);
        if (known) return known;
        return humanizeMachineLabel(rule.replace(/^local-llm-/, ""));
    },
    // Default formatter. Strings/numbers pass through; objects and arrays
    // (e.g. a Linear issue's `labels`: [{name,…}]) render human-readably
    // instead of "[object Object]" — pull a name-ish field per element and
    // join, falling back to JSON so nothing ever shows the raw object tag.
    text: (v) => {
        if (v == null) return "";
        if (typeof v !== "object") return String(v);
        const one = (x) => {
            if (x == null) return "";
            if (typeof x !== "object") return String(x);
            return x.name ?? x.label ?? x.title ?? x.text ?? x.value ?? JSON.stringify(x);
        };
        return Array.isArray(v) ? v.map(one).filter((s) => s !== "").join(", ") : one(v);
    },
};

export function formatValue(value, name) {
    if (name == null) return formatters.text(value);
    const fn = formatters[name];
    if (!fn) return formatters.text(value);
    return fn(value);
}

// Intl.NumberFormat construction is costly enough to matter when called
// per tile per render — keep one compact instance.
let _compactFmt = null;
function compactFmt() {
    if (!_compactFmt) {
        // Pinned to en-US (like renderers.formatCompact) so the K/M/B
        // suffixes stay stable instead of localising to "Mio."/"Mrd." on a
        // differently-localed runtime.
        // 3 significant digits, not a fixed 1 decimal: a fixed decimal
        // rounds 7.98B to a misleadingly round "8B", whereas 3 sig figs
        // keeps it honest ("7.98B") while staying short across scales
        // (29.2K, 8.07M, 240M). Trailing zeros are trimmed.
        _compactFmt = new Intl.NumberFormat("en-US", {
            notation: "compact",
            maximumSignificantDigits: 3,
        });
    }
    return _compactFmt;
}

// Scale-aware humanization for a glanceable hero number. Values under
// 1,000 stay exact (integers as-is, decimals to 2dp); larger magnitudes
// abbreviate to 3 significant figures — 29.2K, 8.07M, 7.98B — so a
// multi-million token count doesn't overflow a narrow KPI tile.
function humanizeNumber(n) {
    if (Math.abs(n) < 1000) {
        return Number.isInteger(n)
            ? n.toString()
            : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return compactFmt().format(n);
}

// formatValue's compact sibling for hero cells (KPI / stat tiles). Only
// numeric formats abbreviate — `number`, `integer`, or an unspecified
// format over a numeric value; percent / duration / date / text already
// read short, so they pass straight through. The exact value stays
// recoverable via formatValue (the tiles keep it on hover).
export function formatCompactValue(value, name) {
    if (name && name !== "number" && name !== "integer") return formatValue(value, name);
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return formatValue(value, name);
    return humanizeNumber(n);
}

// Display tone for deltas / values. Each tone resolves to a CSS color
// at render time; expose them as named constants so kernels say
// `"tone": "good"` rather than embedding raw colors.
export const TONES = {
    neutral: "var(--ink)",
    good: "var(--green)",
    bad: "var(--red, #c0392b)",
    muted: "var(--muted)",
};

export function toneColor(name) {
    return TONES[name] || TONES.neutral;
}

// Best-effort numeric coercion that respects nulls. Used by primitives
// that need a number (sparkline values, bar lengths) without choking
// on strings or undefineds.
export function asNumber(v) {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}
